import { useMemo, useState } from 'react';
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Download, FileJson, RefreshCw, PackagePlus, ArrowUpRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/efs/PageHeader';
import { StatusDot } from '@/components/efs/StatusDot';
import {
  ExceptionSeverityBadge,
  type ExceptionSeverity,
} from '@/components/efs/ExceptionSeverityBadge';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import type { CurrentUser } from '@/lib/permissions';
import { canAccessPath } from '@/lib/role-access';
import {
  AsyncStateBoundary,
  LocalizedApiError,
  isCatalogedIssueCode,
  issueCodeKeys,
  resolveIssueNavigation,
} from '@/features/audit-issues';

import { usePrecheck, usePackages, useGeneratePackage, useManifest } from './useAuditPackage';
import { downloadPackageUrl } from './api';
import type {
  AllDestinationsApprovedCheckDetail,
  AuditPackageDto,
  BatchClosedCheckDetail,
  NoBlockingExceptionsCheckDetail,
  PrecheckItem,
  ReferencedFilesOnDiskDetail,
  RequiredEvidenceCheckDetail,
  StateCheckDetail,
} from './types';

const NS = 'audit-package';

/** i18n `t` shape shared by translation-consuming helpers below. */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Context needed to render the permission-aware precheck detail action (spec-13 §8.4). */
interface CheckDetailCtx {
  user: CurrentUser | null;
  navigate: NavigateFunction;
  batchId: string;
}

const precheckStatusLabel = (t: TFn, c: PrecheckItem): string =>
  t(`efs.audit_package.check_status.${c.passed ? 'passed' : 'failed'}`);

const precheckBlockingLabel = (t: TFn, c: PrecheckItem, blockingReasons: Set<string>): string =>
  t(
    `efs.audit_package.check_status.${
      !c.passed && blockingReasons.has(c.key) ? 'blocking' : 'non_blocking'
    }`,
  );

/**
 * Business-safe structured detail for a failing precheck row (spec-13 §8.4).
 * Only known, allowlisted fields are rendered — never a storage path/UUID.
 */
/**
 * Permission-aware safe action for a failed check (round-6 item 2): the
 * allowlisted destination when the viewer may open it, else the localized
 * permission / unavailable explanation — never silence.
 */
const renderCheckAction = (
  t: TFn,
  ctx: CheckDetailCtx,
  intent: Parameters<typeof resolveIssueNavigation>[0],
  labelKey: string,
) => {
  const resolved = resolveIssueNavigation(intent, ctx.batchId);
  if (!resolved.ok) {
    return (
      <p className="text-efs-text-muted">{t('efs.issues.target.unavailable', { ns: 'issues' })}</p>
    );
  }
  if (!canAccessPath(ctx.user, resolved.path)) {
    return (
      <p className="text-efs-text-muted">
        {t('efs.issues.target.no_permission', { ns: 'issues' })}
      </p>
    );
  }
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      className="h-auto min-h-6 p-0 text-xs"
      onClick={() => ctx.navigate(resolved.path)}
    >
      {t(labelKey, labelKey.startsWith('efs.issues.') ? { ns: 'issues' } : undefined)}
      <ArrowUpRight className="ml-1 h-3 w-3" aria-hidden />
    </Button>
  );
};

/** Safe destination per failed check key (spec-13 §8.4 correction action). */
const CHECK_ACTIONS: Record<string, { kind: string; section?: string; labelKey: string }> = {
  batch_closed: { kind: 'monthly_batch', labelKey: 'efs.audit_package.actions.open_batch' },
  required_evidence: { kind: 'batch_evidence', labelKey: 'efs.audit_package.actions.open_evidence' },
  reconciliation_snapshot_exists: {
    kind: 'batch_mass_balance',
    labelKey: 'efs.audit_package.actions.open_mass_balance',
  },
  close_approval_present: {
    kind: 'batch_approvals',
    labelKey: 'efs.audit_package.actions.open_approvals',
  },
  referenced_files_on_disk: {
    kind: 'batch_evidence',
    labelKey: 'efs.audit_package.actions.open_evidence',
  },
};

const renderCheckDetail = (t: TFn, c: PrecheckItem, ctx: CheckDetailCtx) => {
  if (c.passed) return null;
  const detailRows = renderCheckDetailRows(t, c, ctx);
  const generic = CHECK_ACTIONS[c.key];
  const action = generic
    ? renderCheckAction(
        t,
        ctx,
        { kind: generic.kind, params: { batch_id: ctx.batchId, section: generic.section } },
        generic.labelKey,
      )
    : null;
  if (!detailRows && !action) {
    // Unknown failed check: never silent.
    return (
      <p className="text-efs-text-muted">{t('efs.issues.target.unavailable', { ns: 'issues' })}</p>
    );
  }
  return (
    <div className="space-y-1.5">
      {detailRows}
      {action}
    </div>
  );
};

const renderCheckDetailRows = (t: TFn, c: PrecheckItem, ctx: CheckDetailCtx) => {
  if (c.passed || c.detail == null || typeof c.detail !== 'object') return null;

  if (c.key === 'no_blocking_exceptions') {
    const { affected } = c.detail as NoBlockingExceptionsCheckDetail;
    if (!Array.isArray(affected) || affected.length === 0) return null;
    return (
      <div className="space-y-1.5">
        <ul className="space-y-1">
          {affected.map((row) => (
            <li key={row.exception_id} className="flex flex-wrap items-center gap-2">
              <span>
                {isCatalogedIssueCode(row.issue_code)
                  ? t(issueCodeKeys(row.issue_code).title, { ns: 'issues' })
                  : t('efs.issues.generic.title', { ns: 'issues' })}
              </span>
              <ExceptionSeverityBadge severity={row.severity as ExceptionSeverity} />
            </li>
          ))}
        </ul>
        {renderCheckAction(
          t,
          ctx,
          { kind: 'batch_exceptions', params: { batch_id: ctx.batchId } },
          'efs.issues.target.open_exception',
        )}
      </div>
    );
  }

  if (c.key === 'all_destinations_approved') {
    const { affected } = c.detail as AllDestinationsApprovedCheckDetail;
    if (!Array.isArray(affected) || affected.length === 0) return null;
    return (
      <div className="space-y-1.5">
        <ul className="space-y-1">
          {affected.map((row) => (
            <li key={row.destination_company_id} className="flex flex-wrap items-center gap-2">
              <span className="break-all">{row.name}</span>
              <span className="text-efs-text-muted">
                {t(`efs.common.status.${row.approval_status ?? 'pending'}`, {
                  ns: 'common',
                  defaultValue: row.approval_status ?? '-',
                })}
              </span>
            </li>
          ))}
        </ul>
        {renderCheckAction(
          t,
          ctx,
          { kind: 'master_destination', params: {} },
          'efs.audit_package.actions.open_destinations',
        )}
      </div>
    );
  }

  if (c.key === 'close_approval_present') {
    const { state } = c.detail as StateCheckDetail;
    if (state !== 'missing') return null;
    return <p>{t('efs.audit_package.checks_detail.approval_missing')}</p>;
  }

  if (c.key === 'reconciliation_snapshot_exists') {
    const { state } = c.detail as StateCheckDetail;
    if (state !== 'not_run') return null;
    return <p>{t('efs.audit_package.checks_detail.snapshot_not_run')}</p>;
  }

  if (c.key === 'required_evidence') {
    const { missing } = c.detail as RequiredEvidenceCheckDetail;
    if (!Array.isArray(missing) || missing.length === 0) return null;
    return (
      <p>
        {t('efs.audit_package.messages.missing_evidence', {
          types: missing
            .map((k) => t(`efs.evidence.types.${k}`, { ns: 'evidence', defaultValue: k }))
            .join(', '),
        })}
      </p>
    );
  }

  if (c.key === 'referenced_files_on_disk') {
    const { missing_files: missingFiles } = c.detail as ReferencedFilesOnDiskDetail;
    if (!Array.isArray(missingFiles) || missingFiles.length === 0) return null;
    return (
      <ul className="list-disc space-y-0.5 pl-4">
        {missingFiles.map((f) => (
          <li key={f.file_id}>
            <span className="text-efs-text-muted">
              {t(`efs.audit_package.checks_detail.${f.kind}`)}:
            </span>{' '}
            <span className="break-all">{f.original_file_name}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (c.key === 'batch_closed') {
    const { current_status: currentStatus } = c.detail as BatchClosedCheckDetail;
    if (!currentStatus) return null;
    return (
      <p>
        <span className="text-efs-text-muted">{t('efs.audit_package.fields.status')}:</span>{' '}
        {t(`efs.common.status.${currentStatus}`, { ns: 'common', defaultValue: currentStatus })}
      </p>
    );
  }

  return null;
};

const FOLDER_KEYS = [
  '01-monthly-batch',
  '02-source-files',
  '03-masters-snapshot',
  '04-inbound',
  '05-operations',
  '06-inventory',
  '07-evidence',
  '08-mass-balance',
  '09-approvals',
  '10-audit-log',
];

interface ManifestFolderSummary {
  folder: string;
  file_count: number;
}

interface ManifestSummary {
  year_month?: string | null;
  package_version?: number | null;
  spec_version?: string | null;
  file_count?: number | null;
  total_size_bytes?: number | null;
  generated_at?: string | null;
  closed_at?: string | null;
  contents: ManifestFolderSummary[];
}

const manifestSummaryFrom = (value: unknown): ManifestSummary | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const contents = Array.isArray(record.contents)
    ? record.contents
        .map((item): ManifestFolderSummary | null => {
          if (!item || typeof item !== 'object') return null;
          const folder = (item as Record<string, unknown>).folder;
          const fileCount = (item as Record<string, unknown>).file_count;
          if (typeof folder !== 'string') return null;
          return { folder, file_count: typeof fileCount === 'number' ? fileCount : 0 };
        })
        .filter((item): item is ManifestFolderSummary => item !== null)
    : [];
  return {
    year_month: typeof record.year_month === 'string' ? record.year_month : null,
    package_version: typeof record.package_version === 'number' ? record.package_version : null,
    spec_version: typeof record.spec_version === 'string' ? record.spec_version : null,
    file_count: typeof record.file_count === 'number' ? record.file_count : null,
    total_size_bytes: typeof record.total_size_bytes === 'number' ? record.total_size_bytes : null,
    generated_at: typeof record.generated_at === 'string' ? record.generated_at : null,
    closed_at: typeof record.closed_at === 'string' ? record.closed_at : null,
    contents,
  };
};

const formatBytes = (bytes: number | null | undefined): string => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes)) return '-';
  return `${bytes.toLocaleString()} bytes`;
};

export const AuditPackagePage = () => {
  const { t } = useTranslation([NS, 'common', 'evidence', 'issues']);
  const { batchId } = useParams();
  const user = useCurrentUser();
  const navigate = useNavigate();

  const precheck = usePrecheck(batchId);
  const list = usePackages(batchId);
  const generate = useGeneratePackage();
  const [manifestId, setManifestId] = useState<string | undefined>(undefined);
  const manifest = useManifest(manifestId);

  const canGenerate = hasPermission(user, 'audit_package.manage');
  const showTechnicalDetails = hasPermission(user, 'system.admin');

  const folderCounts = useMemo(() => {
    const latest = list.data?.[0];
    if (!latest) return {} as Record<string, number>;
    const map: Record<string, number> = {};
    const contents = manifestSummaryFrom(latest.manifest_summary)?.contents ?? [];
    for (const c of contents) {
      map[c.folder] = c.file_count;
    }
    return map;
  }, [list.data]);

  if (!batchId) {
    return (
      <PageHeader
        title={t('efs.audit_package.title')}
        subtitle={t('efs.common.messages.no_batch_selected', {
          ns: 'common',
          defaultValue: 'バッチが選択されていません。',
        })}
      />
    );
  }

  const pre = precheck.data;
  const allPassed = pre?.can_generate ?? false;
  const nextVersion = (pre?.latest_version ?? 0) + 1;

  const onGenerate = () => {
    if (!batchId || generate.isPending) return;
    generate.mutate(batchId);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.audit_package.title')}
        subtitle={t('efs.audit_package.subtitle')}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              precheck.refetch();
              list.refetch();
            }}
            disabled={precheck.isFetching || list.isFetching}
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            {t('efs.audit_package.actions.refresh')}
          </Button>
        }
      />

      {/* Section 1: precheck */}
      <Card>
        <CardHeader>
          <CardTitle>{t('efs.audit_package.sections.precheck')}</CardTitle>
        </CardHeader>
        <CardContent>
          <AsyncStateBoundary
            isLoading={precheck.isLoading}
            isError={precheck.isError}
            error={precheck.error}
            isFetching={precheck.isFetching}
            data={precheck.data}
            onRetry={() => precheck.refetch()}
          >
            {(data) => (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('efs.audit_package.fields.status')}</TableHead>
                      <TableHead>{t('efs.audit_package.sections.precheck')}</TableHead>
                      <TableHead className="text-right">
                        {t('efs.audit_package.fields.file_count')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.checks.flatMap((c) => {
                      const blockingReasons = new Set(data.blocking_reasons ?? []);
                      const isBlocking = !c.passed && blockingReasons.has(c.key);
                      const detail = renderCheckDetail(t, c, { user, navigate, batchId });
                      const rows = [
                        <TableRow key={c.key}>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex items-center gap-2 text-xs font-medium text-efs-text-primary">
                                <StatusDot tone={c.passed ? 'success' : 'error'} />
                                {precheckStatusLabel(t, c)}
                              </span>
                              <Badge variant={isBlocking ? 'destructive' : 'outline'}>
                                {precheckBlockingLabel(t, c, blockingReasons)}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">
                                {t(`efs.audit_package.checks.${c.key}`)}
                              </div>
                              <p className="text-xs text-efs-text-muted">
                                {t(`efs.audit_package.check_explanations.${c.key}`)}
                              </p>
                              <p className="text-xs text-efs-text-muted">
                                {t(`efs.audit_package.check_guidance.${c.key}`)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {c.count != null ? c.count : '-'}
                          </TableCell>
                        </TableRow>,
                      ];
                      if (detail) {
                        rows.push(
                          <TableRow key={`${c.key}-detail`}>
                            <TableCell colSpan={3} className="pt-0 text-xs text-efs-text-muted">
                              {detail}
                            </TableCell>
                          </TableRow>,
                        );
                      }
                      return rows;
                    })}
                  </TableBody>
                </Table>

                {data.evidence_completeness.missing > 0 ? (
                  <Alert variant="destructive" className="mt-4">
                    <AlertTitle>{t('efs.audit_package.fields.missing_types')}</AlertTitle>
                    <AlertDescription>
                      {t('efs.audit_package.messages.missing_evidence', {
                        types: Object.keys(data.evidence_completeness.required_by_type ?? {})
                          .filter(
                            (k) =>
                              !(data.evidence_completeness.uploaded as Record<string, number>)?.[k],
                          )
                          // Show the localized evidence-type label, not the raw token.
                          .map((k) =>
                            t(`efs.evidence.types.${k}`, { ns: 'evidence', defaultValue: k }),
                          )
                          .join(', '),
                      })}
                    </AlertDescription>
                  </Alert>
                ) : null}
              </>
            )}
          </AsyncStateBoundary>
        </CardContent>
      </Card>

      {/* Section 2: folder preview */}
      <Card>
        <CardHeader>
          <CardTitle>{t('efs.audit_package.sections.structure')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('efs.audit_package.fields.folder')}</TableHead>
                <TableHead className="text-right">
                  {t('efs.audit_package.fields.file_count')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {FOLDER_KEYS.map((f) => (
                <TableRow key={f}>
                  <TableCell>{t(`efs.audit_package.folders.${f}`, { defaultValue: f })}</TableCell>
                  <TableCell className="text-right">{folderCounts[f] ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Section 3: generate */}
      <Card>
        <CardHeader>
          <CardTitle>{t('efs.audit_package.sections.generate')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!allPassed ? (
            <Alert variant="destructive">
              <AlertTitle>{t('efs.audit_package.messages.blocked')}</AlertTitle>
              <AlertDescription>
                {(pre?.blocking_reasons ?? [])
                  // Blocking reasons are check keys — reuse the check labels.
                  .map((r) => t(`efs.audit_package.checks.${r}`))
                  .join(', ')}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertTitle>{t('efs.audit_package.messages.ready')}</AlertTitle>
              <AlertDescription>
                {t('efs.audit_package.messages.regenerate_warning', { next: nextVersion })}
              </AlertDescription>
            </Alert>
          )}

          {canGenerate ? (
            <Button onClick={onGenerate} disabled={!allPassed || generate.isPending}>
              {generate.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <PackagePlus className="mr-1 h-4 w-4" />
              )}
              {generate.isPending
                ? t('efs.audit_package.actions.generating')
                : t('efs.audit_package.actions.generate')}
            </Button>
          ) : null}

          {generate.isPending ? (
            <p className="text-sm text-efs-text-muted">
              {t('efs.audit_package.messages.in_flight')}
            </p>
          ) : null}

          {generate.isError ? (
            <LocalizedApiError error={generate.error} showTechnicalDetails={showTechnicalDetails} />
          ) : null}
        </CardContent>
      </Card>

      {/* Section 4: versions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('efs.audit_package.sections.versions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <AsyncStateBoundary
            isLoading={list.isLoading}
            isError={list.isError}
            error={list.error}
            isFetching={list.isFetching}
            data={list.data}
            isEmpty={(data) => data.length === 0}
            onRetry={() => list.refetch()}
            emptyContent={
              <p className="text-sm text-efs-text-muted">
                {t('efs.audit_package.messages.no_versions')}
              </p>
            }
          >
            {(data) => (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('efs.audit_package.fields.version')}</TableHead>
                    <TableHead>{t('efs.audit_package.fields.generated_at')}</TableHead>
                    <TableHead className="text-right">
                      {t('efs.audit_package.fields.file_count')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.audit_package.fields.total_size')}
                    </TableHead>
                    <TableHead>{t('efs.audit_package.fields.status')}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((p: AuditPackageDto) => (
                    <TableRow key={p.audit_package_id}>
                      <TableCell>v{p.version}</TableCell>
                      <TableCell>
                        <time dateTime={p.generated_at}>{p.generated_at}</time>
                      </TableCell>
                      <TableCell className="text-right">{p.file_count}</TableCell>
                      <TableCell className="text-right">{p.total_size_bytes}</TableCell>
                      <TableCell>
                        {p.superseded_at ? (
                          <Badge variant="outline">
                            {t('efs.audit_package.fields.superseded')}
                          </Badge>
                        ) : (
                          <Badge>{t('efs.audit_package.fields.current')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="space-x-1">
                        <Button asChild variant="outline" size="sm">
                          <a
                            href={downloadPackageUrl(batchId, p.version)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Download className="mr-1 h-3 w-3" />
                            {t('efs.audit_package.actions.download')}
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setManifestId(p.audit_package_id)}
                        >
                          <FileJson className="mr-1 h-3 w-3" />
                          {t('efs.audit_package.actions.view_manifest')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </AsyncStateBoundary>
        </CardContent>
      </Card>

      {/* Section 5: manifest dialog */}
      <Dialog open={!!manifestId} onOpenChange={(open) => !open && setManifestId(undefined)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('efs.audit_package.sections.manifest')}</DialogTitle>
            <DialogDescription>
              {t('efs.audit_package.messages.manifest_summary_description')}
            </DialogDescription>
          </DialogHeader>
          {manifest.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : manifest.isError ? (
            <LocalizedApiError error={manifest.error} showTechnicalDetails={showTechnicalDetails} />
          ) : manifest.data ? (
            <ManifestSummaryView summary={manifestSummaryFrom(manifest.data)} />
          ) : (
            <p className="text-sm text-efs-text-muted">
              {t('efs.audit_package.messages.manifest_unavailable')}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ManifestSummaryView = ({ summary }: { summary: ManifestSummary | null }) => {
  const { t } = useTranslation(NS);
  if (!summary) {
    return (
      <p className="text-sm text-efs-text-muted">
        {t('efs.audit_package.messages.manifest_unavailable')}
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <p className="text-xs text-efs-text-muted">{t('efs.audit_package.fields.year_month')}</p>
          <p className="font-medium text-efs-text-primary">{summary.year_month ?? '-'}</p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">{t('efs.audit_package.fields.version')}</p>
          <p className="font-medium text-efs-text-primary">
            {summary.package_version != null ? `v${summary.package_version}` : '-'}
          </p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">{t('efs.audit_package.fields.file_count')}</p>
          <p className="font-medium text-efs-text-primary">{summary.file_count ?? '-'}</p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">{t('efs.audit_package.fields.total_size')}</p>
          <p className="font-medium text-efs-text-primary">
            {formatBytes(summary.total_size_bytes)}
          </p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">
            {t('efs.audit_package.fields.generated_at')}
          </p>
          <p className="font-medium text-efs-text-primary">{summary.generated_at ?? '-'}</p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">
            {t('efs.audit_package.fields.spec_version')}
          </p>
          <p className="font-medium text-efs-text-primary">{summary.spec_version ?? '-'}</p>
        </div>
      </div>
      {summary.contents.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.audit_package.fields.folder')}</TableHead>
              <TableHead className="text-right">
                {t('efs.audit_package.fields.file_count')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.contents.map((item) => (
              <TableRow key={item.folder}>
                <TableCell>
                  {t(`efs.audit_package.folders.${item.folder}`, { defaultValue: item.folder })}
                </TableCell>
                <TableCell className="text-right">{item.file_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="text-sm text-efs-text-muted">
          {t('efs.audit_package.messages.manifest_contents_empty')}
        </p>
      )}
    </div>
  );
};

export default AuditPackagePage;
